import { get, find, last, isEmpty, reduce, set, includes, keys, flatten } from 'lodash'
import { getTreeNodes } from 'utils/parameters'
import { asNumber } from 'utils/tealium'
import TreeNodeResolver from 'routes/Filter/SearchParam/Tree/TreeNodeResolver'

const createNextLevelCategoryString = (categoryLabel) =>
  categoryLabel ? ` > ${categoryLabel}` : ''

const createCarModelPath = (carModel) => {
  if (!carModel) return ''
  const carModelLevel1 = createNextLevelCategoryString(get(carModel, '1.label'))
  const carModelLevel2 = createNextLevelCategoryString(get(carModel, '2.label'))
  const carModelLevel3 = createNextLevelCategoryString(get(carModel, '3.label'))
  return `${carModelLevel1}${carModelLevel2}${carModelLevel3}`
}

const createCategoryPath = (category, carModel, countryLabel) => {
  const noCategory = get(category, '0.label', 'Alla')
  const categoryLevel1 = get(category, '1.label', '')
  const categoryLevel2 = createNextLevelCategoryString(get(category, '2.label'))
  const categoryLevel3 = createNextLevelCategoryString(get(category, '3.label'))
  const carModelPath = createCarModelPath(carModel)
  const countryPath = createNextLevelCategoryString(countryLabel)
  const categoryPath = `${categoryLevel1}${categoryLevel2}${categoryLevel3}${carModelPath}${countryPath}`

  return isEmpty(categoryPath) ? noCategory : categoryPath.replace(/,/g, '')
}

const getChosenParameters = (params, func, query) => {
  const parameters = reduce(
    get(find(params, func), 'parameters'),
    (result, value) => {
      set(result, value.queryKey, value.label)
      return result
    },
    {},
  )
  if (isEmpty(parameters)) {
    return undefined
  }
  const chosenParameters = reduce(
    query,
    (result, value, key) => {
      if (includes(keys(parameters), key)) {
        result.push(parameters[key])
      }
      return result
    },
    [],
  )
  if (isEmpty(chosenParameters)) {
    return undefined
  }
  return chosenParameters
}

const getRangeLabel = (params, func, name, value) =>
  get(find(get(find(params, func), `${name}.items`), (o) => o.value === value), 'label')

const getLabel = (params, name, value) =>
  get(find(get(params, `${name}.items`), (o) => o.value === value), 'label')

const searchParameters = (searchParams, query) => {
  if (isEmpty(query)) {
    return {}
  }
  const location = getTreeNodes(get(searchParams, 'byQueryKey.r'), query)
  const category = getTreeNodes(get(searchParams, 'byQueryKey.cg'), query)

  const getParams = {
    r: flatten([query.r]).filter((item) => item),
    m: flatten([query.m]).filter((item) => item),
    sa: flatten([query.sa]).filter((item) => item),
  }

  const regionParameter = get(searchParams, 'byQueryKey.r')

  const getParamsResolved = query.r && TreeNodeResolver(regionParameter)(getParams)

  const label = (parameterLevel) => {
    switch (parameterLevel) {
      case 'sa':
        return getParamsResolved[parameterLevel]
          .map(
            (sa) =>
              `${getParamsResolved.r[0].label} > ${
                getParamsResolved.m[0].label
              } > ${sa.label.replace(/,/g, '')}`,
          )
          .join(', ')
      case 'm':
        return getParamsResolved[parameterLevel]
          .map((m) => `${getParamsResolved.r[0].label} > ${m.label}`)
          .join(', ')
      case 'r':
        return getParamsResolved[parameterLevel].map((r) => `${r.label}`).join(', ')
      default:
        return ''
    }
  }

  const carBrandParams = get(searchParams, 'byQueryKey.cb')
  const carModel = carBrandParams && getTreeNodes(carBrandParams, query)

  const countryParams = get(searchParams, 'byQueryKey.co')
  const countryLabel = countryParams && get(find(countryParams.items, { value: query.co }), 'label')

  const lastCategoryValue = get(last(category), 'value')
  const objectCategories = lastCategoryValue && [{ localId: lastCategoryValue }]
  return {
    objectFilterNearbyRegions: asNumber(query.ag),
    objectFilterRegionPart: get(location, '2.label'),
    objectFilterRegionPartId: asNumber(get(location, '2.value')),
    objectFilterOnlyLoweredPrice: query.plo ? 1 : undefined,
    objectFilterOnlyBlocketPaketet: query.sh ? 1 : undefined,
    objectFilterPremiumWarranty: query.pw ? 1 : undefined,
    objectFilterPriceMin: getRangeLabel(
      searchParams.parameters,
      (o) => o.label === 'Pris',
      'rangeStart',
      query.ps,
    ),
    objectFilterPriceMax: getRangeLabel(
      searchParams.parameters,
      (o) => o.label === 'Pris',
      'rangeEnd',
      query.pe,
    ),
    objectFilterYearMin: query.mys || query.rs,
    objectFilterYearMax: query.mye || query.re,
    objectFilterCarPowerMin: getRangeLabel(
      searchParams.parameters,
      (o) => o.id === 'cars_engine_power',
      'rangeStart',
      query.cxpf,
    ),
    objectFilterCarPowerMax: getRangeLabel(
      searchParams.parameters,
      (o) => o.id === 'cars_engine_power',
      'rangeEnd',
      query.cxpt,
    ),
    objectFilterCarKmMin: getRangeLabel(
      searchParams.parameters,
      (o) => o.id === 'cars_mileage',
      'rangeStart',
      query.ms,
    ),
    objectFilterCarKmMax: getRangeLabel(
      searchParams.parameters,
      (o) => o.id === 'cars_mileage',
      'rangeEnd',
      query.me,
    ),
    objectFilterCarFuel: getLabel(searchParams.byQueryKey, 'fu', query.fu),
    objectFilterCarGearbox: getLabel(searchParams.byQueryKey, 'gb', query.gb),
    objectFilterCar4WheelDrive: asNumber(query.cxdw),
    objectFilterCarColors: getChosenParameters(
      searchParams.parameters,
      (o) => o.id === 'car_colors',
      query,
    ),
    objectFilterCarChassis: getChosenParameters(
      searchParams.parameters,
      (o) => o.id === 'car_chassis',
      query,
    ),
    objectFilterCarHasInsurance: asNumber(query.bbf),
    objectFilterCarBrand: get(carModel, '1.label'),
    objectFilterCarBrandId: asNumber(get(carModel, '1.value')),
    objectFilterCarModelLevel1: get(carModel, '2.label'),
    objectFilterCarModelLevel1Id: asNumber(get(carModel, '2.value')),
    objectFilterCarModelLevel2: get(carModel, '3.label'),
    objectFilterCarModelLevel2Id: asNumber(get(carModel, '3.value')),
    objectFilterMotorbikeNoQuad: asNumber(query.no_atv),
    objectFilterRealEstateSurfaceMin: getRangeLabel(
      searchParams.parameters,
      (o) => o.id === 'floor_area_homes',
      'rangeStart',
      query.ss,
    ),
    objectFilterRealEstateSurfaceMax: getRangeLabel(
      searchParams.parameters,
      (o) => o.id === 'floor_area_homes',
      'rangeEnd',
      query.se,
    ),
    objectFilterRealEstateRoomsMin: getRangeLabel(
      searchParams.parameters,
      (o) => o.id === 'rooms',
      'rangeStart',
      query.ros,
    ),
    objectFilterRealEstateRoomsMax: getRangeLabel(
      searchParams.parameters,
      (o) => o.id === 'rooms',
      'rangeEnd',
      query.roe,
    ),
    objectFilterRealEstateBedsMin: query.bs,
    objectFilterRealEstateBedsMax: query.be,
    objectFilterRealEstateMaxMounthlyRent: query.mre,

    objectFilterClothingGender: getLabel(searchParams.byQueryKey, 'cs', query.cs),
    objectFilterClothingSize: getLabel(searchParams.byQueryKey, 'csz', query.csz),
    objectFilterClothingKind: getLabel(searchParams.byQueryKey, 'ck', query.ck),
    // region

    objectFilterRegionPath: `${((item) => (item ? `${item}` : ''))(get(location, '1.label'))}${((
      item,
    ) => (item ? ` > ${item}` : ''))(get(location, '2.label'))}${((item) =>
      item ? ` > ${item}` : '')(get(location, '3.label'))}`.replace(/,/g, ''),
    objectCategoryPath: createCategoryPath(category, carModel, countryLabel),
    objectCategories,
    objectFilterMultiRegionPath:
      getParamsResolved && getParamsResolved.parameter_level
        ? label(getParamsResolved.parameter_level)
        : 'Hela Sverige',
  }
}

export default searchParameters
